import { useCallback, useEffect, useRef, useState } from 'react';
import { Bell, CalendarCheck, CalendarPlus, RefreshCw, Unplug } from 'lucide-react';
import { api } from '../App';
import { toast } from 'sonner';

const timeAgo = (value) => {
  const seconds = Math.max(1, Math.floor((Date.now() - new Date(value).getTime()) / 1000));
  if (seconds < 60) return 'Just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
};

export default function AccountTools({ onNavigate }) {
  const [calendar, setCalendar] = useState({ connected: false, email: '' });
  const [notifications, setNotifications] = useState([]);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const rootRef = useRef(null);

  const loadCalendar = useCallback(async () => {
    try {
      const response = await api.get('/calendar/status');
      setCalendar(response.data);
    } catch (error) {
      console.error('Calendar status failed', error);
    }
  }, []);

  const loadNotifications = useCallback(async () => {
    try {
      const response = await api.get('/notifications?limit=30');
      setNotifications(response.data);
    } catch (error) {
      console.error('Notifications failed', error);
    }
  }, []);

  useEffect(() => {
    loadCalendar();
    loadNotifications();
    const timer = window.setInterval(loadNotifications, 30000);
    const params = new URLSearchParams(window.location.search);
    const result = params.get('calendar');
    if (result) {
      if (result === 'connected') toast.success('Google Calendar connected');
      if (result === 'cancelled') toast.info('Calendar connection cancelled');
      if (result === 'error') toast.error('Google Calendar could not be connected');
      window.history.replaceState({}, '', window.location.pathname);
    }
    return () => window.clearInterval(timer);
  }, [loadCalendar, loadNotifications]);

  useEffect(() => {
    const close = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setCalendarOpen(false);
        setNotificationsOpen(false);
      }
    };
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, []);

  const connectCalendar = async () => {
    try {
      setBusy(true);
      const response = await api.get('/auth/google/login');
      window.location.assign(response.data.authorization_url);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Google Calendar could not be connected');
      setBusy(false);
    }
  };

  const syncCalendar = async () => {
    try {
      setBusy(true);
      const response = await api.post('/calendar/sync');
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Calendar sync failed');
    } finally {
      setBusy(false);
    }
  };

  const disconnectCalendar = async () => {
    try {
      setBusy(true);
      await api.delete('/calendar/disconnect');
      setCalendar({ connected: false, email: '' });
      setCalendarOpen(false);
      toast.success('Google Calendar disconnected');
    } catch (error) {
      toast.error('Calendar could not be disconnected');
    } finally {
      setBusy(false);
    }
  };

  const openNotification = async (notification) => {
    if (!notification.read) {
      await api.put(`/notifications/${notification.id}/read`);
      setNotifications((items) => items.map((item) => item.id === notification.id ? { ...item, read: true } : item));
    }
    if (notification.action_url) onNavigate(notification.action_url);
    setNotificationsOpen(false);
  };

  const markAllRead = async () => {
    await api.put('/notifications/read-all');
    setNotifications((items) => items.map((item) => ({ ...item, read: true })));
  };

  const unreadCount = notifications.filter((item) => !item.read).length;

  return (
    <div ref={rootRef} className="relative flex items-center gap-2">
      <div className="relative">
        <button
          type="button"
          className="relative flex h-9 w-9 items-center justify-center border bg-white hover:bg-slate-50"
          onClick={() => { setNotificationsOpen((value) => !value); setCalendarOpen(false); }}
          aria-label="Notifications"
          title="Notifications"
          data-testid="notifications-button"
        >
          <Bell size={17} />
          {unreadCount > 0 && <span className="absolute -right-1 -top-1 min-w-4 bg-red-600 px-1 text-center text-[10px] font-bold leading-4 text-white">{Math.min(unreadCount, 99)}</span>}
        </button>
        {notificationsOpen && (
          <div className="fixed left-3 right-3 top-20 z-[70] max-h-[70vh] overflow-hidden border bg-white shadow-xl sm:absolute sm:left-auto sm:right-0 sm:top-11 sm:w-96">
            <div className="flex items-center justify-between border-b px-4 py-3">
              <strong>Notifications</strong>
              {unreadCount > 0 && <button className="text-xs text-blue-700 hover:underline" onClick={markAllRead}>Mark all read</button>}
            </div>
            <div className="max-h-[60vh] overflow-y-auto">
              {notifications.length === 0 ? (
                <p className="px-4 py-8 text-center text-sm text-slate-500">No alerts yet</p>
              ) : notifications.map((notification) => (
                <button key={notification.id} onClick={() => openNotification(notification)} className={`block w-full border-b px-4 py-3 text-left hover:bg-slate-50 ${notification.read ? '' : 'bg-blue-50'}`}>
                  <span className="flex items-start gap-3">
                    <span className={`mt-1 h-2 w-2 shrink-0 ${notification.read ? 'bg-slate-300' : 'bg-blue-600'}`} />
                    <span className="min-w-0"><span className="block text-sm font-semibold">{notification.title}</span><span className="mt-1 block text-sm text-slate-600">{notification.message}</span><span className="mt-1 block text-xs text-slate-400">{timeAgo(notification.created_at)}</span></span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="relative">
        <button
          type="button"
          className={`flex h-9 items-center gap-2 border px-2.5 text-sm hover:bg-slate-50 ${calendar.connected ? 'text-green-700' : ''}`}
          onClick={() => { if (calendar.connected) { setCalendarOpen((value) => !value); setNotificationsOpen(false); } else { connectCalendar(); } }}
          disabled={busy}
          title={calendar.connected ? 'Google Calendar connected' : 'Connect Google Calendar'}
          data-testid="calendar-control"
        >
          {calendar.connected ? <CalendarCheck size={17} /> : <CalendarPlus size={17} />}
          <span className="hidden lg:inline">{calendar.connected ? 'Calendar synced' : 'Connect calendar'}</span>
        </button>
        {calendarOpen && (
          <div className="absolute right-0 top-11 z-[70] w-72 border bg-white p-4 shadow-xl">
            <div className="flex gap-3"><CalendarCheck className="mt-0.5 text-green-600" size={19} /><div className="min-w-0"><p className="font-semibold">Google Calendar connected</p><p className="truncate text-xs text-slate-500">{calendar.email}</p></div></div>
            <button className="mt-4 flex w-full items-center gap-2 border px-3 py-2 text-sm hover:bg-slate-50" onClick={syncCalendar} disabled={busy}><RefreshCw size={15} /> Sync upcoming interviews</button>
            <button className="mt-2 flex w-full items-center gap-2 px-3 py-2 text-sm text-red-700 hover:bg-red-50" onClick={disconnectCalendar} disabled={busy}><Unplug size={15} /> Disconnect</button>
          </div>
        )}
      </div>
    </div>
  );
}
